import { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from 'react'
import {
  FileUploadPassthroughProps,
  FileUploadReturnType,
  ProcessedFileFormats
} from './FileUpload'
import { blobToBase64 } from '../../utils/fileUtils'

type UseFileUploadReturn = {
  fileName: string
  errorMessage: string
  hiddenRef: MutableRefObject<HTMLInputElement | null>
  handleFileSelection: (e: ChangeEvent<HTMLInputElement>) => void
  clearFileSelection: () => void
}

export const useFileUpload = ({
  onChange,
  accept,
  maxFileSizeMB = 10,
  returnType = FileUploadReturnType.FILE
}: FileUploadPassthroughProps): UseFileUploadReturn => {
  const hiddenRef = useRef<HTMLInputElement | null>(null)
  const [fileToProcess, setFileToProcess] = useState<File | undefined>(undefined)
  const [fileName, setFileName] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [processedFile, setProcessedFile] = useState<ProcessedFileFormats | undefined>(undefined)
  const [lastReturnType, setLastReturnType] = useState<FileUploadReturnType | undefined>(undefined)

  const rejectFileSelection = (
    e: ChangeEvent<HTMLInputElement>,
    newErrorMessage?: string
  ): void => {
    setFileToProcess(undefined)
    setProcessedFile(undefined)
    setFileName('')
    e.target.value = ''
    if (newErrorMessage) setErrorMessage(newErrorMessage)
  }

  const handleFileSelection = (e: ChangeEvent<HTMLInputElement>): void => {
    setErrorMessage('')
    const file = e?.target?.files?.[0]
    const { name, type, size } = file ?? {}
    const isCompleteFile = name && type && size
    const isAcceptable = accept && type ? accept.split(',').includes(type) : true
    const BYTES_PER_MB = 1000000
    const selectedFileSizeInMB = size ? size / BYTES_PER_MB : 0
    const roundedFileSizeInMB = Math.round(selectedFileSizeInMB * 10) / 10
    const isWithinSizeLimits = selectedFileSizeInMB <= maxFileSizeMB

    if (!file) rejectFileSelection(e, '')
    else if (!isCompleteFile)
      rejectFileSelection(e, 'File is missing required properties: name, type, size')
    else if (!isAcceptable)
      rejectFileSelection(
        e,
        `File type: ${type} is not included in the accepted list of types: ${accept}`
      )
    else if (!isWithinSizeLimits)
      rejectFileSelection(
        e,
        `File size: ${roundedFileSizeInMB} is greater than the max file size (mb): ${maxFileSizeMB}`
      )
    else {
      setFileToProcess(file)
      setFileName(name)
    }
  }

  const clearFileSelection = (): void => {
    setFileToProcess(undefined)
    setFileName('')
    setErrorMessage('')
    setProcessedFile(undefined)
    setLastReturnType(undefined)
    if (onChange) onChange('')
  }

  useEffect(() => {
    if (fileToProcess && !processedFile && returnType !== lastReturnType) {
      switch (returnType) {
        case FileUploadReturnType.FILE:
          setProcessedFile(fileToProcess)
          break
        case FileUploadReturnType.OBJECT_URL:
          const url = URL.createObjectURL(fileToProcess)
          setProcessedFile(url)
          break
        case FileUploadReturnType.BASE64:
          blobToBase64(fileToProcess).then(res => setProcessedFile(res))
          break
      }

      setLastReturnType(returnType)
    }
  }, [fileToProcess, lastReturnType, processedFile, returnType])

  useEffect(() => {
    if (processedFile && onChange) {
      onChange(processedFile)
      setProcessedFile(undefined)
    }
  }, [onChange, processedFile])

  return { fileName, errorMessage, hiddenRef, handleFileSelection, clearFileSelection }
}
